import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash, PencilSimple, ShoppingCart, Link as LinkIcon,
  Archive, ArrowCounterClockwise, MagnifyingGlass, CheckCircle, Clock,
  ClipboardText, X,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useAuth } from "../lib/auth";
import { absTime } from "../lib/labels";

const emptyProduct = () => ({ name: "", type: "", quantity: 1, url: "", purchase_price: "", sale_price: "" });

const fmtPrice = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(v);
  if (Number.isNaN(n)) return null;
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
};

// Convert the browser <input type="date"> value ("YYYY-MM-DD") into a full local
// ISO datetime. Fixes the "midnight UTC → 02:00 Paris" surprise by anchoring the
// stored moment to the requester's current wall-time.
const isoFromDateInput = (dateStr) => {
  if (!dateStr) return null;
  const s = String(dateStr).trim();
  if (!s) return null;
  if (s.includes("T")) return s;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return s;
  const now = new Date();
  const d = new Date(+m[1], +m[2] - 1, +m[3], now.getHours(), now.getMinutes(), now.getSeconds(), 0);
  return d.toISOString();
};

// Display the "date demande" — a plain "YYYY-MM-DD" is rendered as day-only to
// avoid the misleading "02:00" that `new Date("2026-09-15")` would produce.
const dateOrAbs = (iso) => {
  if (!iso) return "—";
  const s = String(iso);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-");
    return `${d}/${m}/${y}`;
  }
  return absTime(s);
};

const emptyForm = () => ({
  client_name: "", requester_tech_id: "", requester_tech_name: "",
  invoice_or_quote_number: "", requested_at: "",
  notes: "", products: [emptyProduct()],
});

const StatusBadge = ({ ordered_at }) => (
  ordered_at ? (
    <span className="text-[0.65rem] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 font-semibold inline-flex items-center gap-1" data-testid="po-badge-ordered">
      <CheckCircle size={11} weight="fill"/> Commandée
    </span>
  ) : (
    <span className="text-[0.65rem] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 font-semibold inline-flex items-center gap-1" data-testid="po-badge-pending">
      <Clock size={11} weight="fill"/> À commander
    </span>
  )
);

const productsPreview = (products) => {
  if (!Array.isArray(products) || products.length === 0) return "—";
  const names = products.map(p => (p.name || "").trim()).filter(Boolean);
  if (names.length === 0) return "—";
  if (names.length === 1) return names[0];
  return `${names[0]} +${names.length - 1} autre${names.length > 2 ? "s" : ""}`;
};

export default function PurchaseOrders() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  // v287 — Pagination serveur (25/page) : reset auto page 1 sur changement
  // de recherche ou de la vue (Actives ↔ Archivées).
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const PO_PER_PAGE = 25;
  const [techs, setTechs] = useState([]);
  const [editor, setEditor] = useState(null);
  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [markModal, setMarkModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const load = () => {
    const params = new URLSearchParams();
    if (showArchived) params.set("archived", "true");
    if (qDebounced) params.set("q", qDebounced);
    params.set("page", String(page));
    params.set("per_page", String(PO_PER_PAGE));
    const qs = params.toString();
    return api.get(`/purchase-orders${qs ? `?${qs}` : ""}`)
      .then((r) => {
        setItems(r.data.items || []);
        setTotal(Number(r.data.total ?? (r.data.items || []).length));
        setPages(Math.max(1, Number(r.data.pages ?? 1)));
      })
      .catch(() => {});
  };
  useEffect(() => { load(); setSelectedIds(new Set()); }, [showArchived, qDebounced, page]); // eslint-disable-line
  // Reset to page 1 whenever the user changes the search or the archive view
  // (avoids landing on a now-empty page N).
  useEffect(() => { setPage(1); }, [qDebounced, showArchived]);
  useEffect(() => {
    api.get("/users").then((r) => setTechs((r.data || []).filter((u) => ["admin", "technicien"].includes(u.role)))).catch(() => {});
  }, []);

  const openCreate = () => setEditor({
    mode: "create", ...emptyForm(),
    requester_tech_id: user?.id || "",
    requester_tech_name: user?.full_name || "",
    requested_at: new Date().toISOString().slice(0, 10),
  });
  const openEdit = (it) => setEditor({
    mode: "edit", id: it.id,
    client_name: it.client_name || "",
    requester_tech_id: it.requester_tech_id || "",
    requester_tech_name: it.requester_tech_name || "",
    invoice_or_quote_number: it.invoice_or_quote_number || "",
    requested_at: (it.requested_at || "").slice(0, 10),
    notes: it.notes || "",
    products: (it.products || []).length
      ? it.products.map(p => ({ ...emptyProduct(), ...p }))
      : [emptyProduct()],
  });

  const setProduct = (idx, patch) => setEditor(e => ({
    ...e,
    products: e.products.map((p, i) => i === idx ? { ...p, ...patch } : p),
  }));
  const addProduct = () => setEditor(e => ({ ...e, products: [...e.products, emptyProduct()] }));
  const removeProduct = (idx) => setEditor(e => ({
    ...e,
    products: e.products.length > 1 ? e.products.filter((_, i) => i !== idx) : e.products,
  }));

  const save = async () => {
    if (!editor.client_name) { toast.error("Le nom du client est requis"); return; }
    const cleanProducts = editor.products
      .map(p => ({ ...p, name: (p.name || "").trim(), type: (p.type || "").trim(), url: (p.url || "").trim() }))
      .filter(p => p.name);
    const toPrice = (v) => {
      if (v === "" || v === null || v === undefined) return null;
      const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
      return Number.isFinite(n) && n >= 0 ? n : null;
    };
    if (cleanProducts.length === 0) { toast.error("Au moins un produit avec un nom est requis"); return; }
    setBusy(true);
    try {
      const body = {
        client_name: editor.client_name.trim(),
        requester_tech_id: editor.requester_tech_id || null,
        requester_tech_name: editor.requester_tech_name || null,
        products: cleanProducts.map(p => ({
          name: p.name, type: p.type || null,
          quantity: Math.max(1, parseInt(p.quantity, 10) || 1),
          url: p.url || null,
          purchase_price: toPrice(p.purchase_price),
          sale_price: toPrice(p.sale_price),
        })),
        invoice_or_quote_number: editor.invoice_or_quote_number || null,
        requested_at: isoFromDateInput(editor.requested_at),
        notes: editor.notes || null,
      };
      if (editor.mode === "create") {
        await api.post("/purchase-orders", body);
        toast.success(`Commande créée · ${cleanProducts.length} produit(s)`);
      } else {
        await api.put(`/purchase-orders/${editor.id}`, body);
        toast.success("Commande modifiée");
      }
      setEditor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/purchase-orders/${id}`); toast.success("Supprimée"); setSelected(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const toggleArchive = async (item, want) => {
    try {
      await api.post(`/purchase-orders/${item.id}/archive`, { archived: want });
      toast.success(want ? "Archivée" : "Restaurée");
      setSelected(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const markOrdered = async () => {
    if (!markModal) return;
    setBusy(true);
    try {
      // v292 — deux modes : "Marquer commandée" (transition initiale, envoie l'email)
      // vs "Modifier le suivi" (édition post-commande, PUT sans re-notifier).
      if (markModal.trackingOnly) {
        const r = await api.put(`/purchase-orders/${markModal.item.id}`,
                                 { tracking_info: markModal.tracking_info || null });
        toast.success("Numéro de suivi mis à jour");
        setMarkModal(null); setSelected(r.data); load();
      } else {
        const r = await api.post(`/purchase-orders/${markModal.item.id}/mark-ordered`,
                                  { tracking_info: markModal.tracking_info || null });
        toast.success(`Marquée commandée le ${absTime(r.data.ordered_at)} — notification envoyée`);
        setMarkModal(null); setSelected(r.data); load();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const unmarkOrdered = async (item) => {
    if (!window.confirm("Annuler le marquage 'commandée' ?")) return;
    try {
      const r = await api.post(`/purchase-orders/${item.id}/unmark-ordered`);
      toast.success("Marquage annulé"); setSelected(r.data); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const bulkArchive = async () => {
    setBusy(true);
    try {
      const r = await api.post("/purchase-orders/bulk-archive",
                                { ids: [...selectedIds], archived: !showArchived });
      toast.success(`${r.data.updated} commande(s) ${showArchived ? "restaurée(s)" : "archivée(s)"}`);
      setConfirmBulk(false); setSelectedIds(new Set()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const pendingCount = useMemo(() => items.filter(i => !i.ordered_at).length, [items]);

  return (
    <div className="p-6 max-w-6xl">
      <button onClick={() => nav("/modules")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="po-back">
        <ArrowLeft size={16}/> Modules
      </button>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h1 className="text-3xl font-display font-bold mb-1 flex items-center gap-2">
            <ShoppingCart size={26}/> Commandes
          </h1>
          <p className="text-sm text-muted-foreground">
            Les techniciens saisissent leurs demandes de commande (plusieurs produits par demande), l'admin marque la commande passée en 1 clic — un email de confirmation part au tech.
            {!showArchived && pendingCount > 0 && <> <span className="text-amber-700 font-semibold">· {pendingCount} en attente</span></>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowArchived(!showArchived)} data-testid="po-toggle-archived">
            {showArchived ? "Voir les actives" : "Voir les archives"}
          </Button>
          <Button onClick={openCreate} data-testid="po-new"><Plus size={14} className="mr-1"/> Nouvelle demande</Button>
        </div>
      </div>

      <div className="relative mb-3">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
        <Input placeholder="Rechercher par nom produit, client, facture, technicien, suivi…"
               value={q} onChange={(e) => setQ(e.target.value)}
               className="pl-9" data-testid="po-search"/>
      </div>

      {selectedIds.size > 0 && (
        <div className="rounded-lg border bg-primary/5 p-2 flex items-center justify-between mb-3">
          <div className="text-sm">{selectedIds.size} sélectionnée(s)</div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>Désélectionner</Button>
            <Button size="sm" onClick={() => setConfirmBulk(true)} data-testid="po-bulk-archive">
              {showArchived
                ? <><ArrowCounterClockwise size={14} className="mr-1"/> Restaurer ({selectedIds.size})</>
                : <><Archive size={14} className="mr-1"/> Archiver ({selectedIds.size})</>}
            </Button>
          </div>
        </div>
      )}

      {items.length === 0 && (
        <div className="rounded-xl border-2 border-dashed p-8 text-center text-muted-foreground" data-testid="po-empty">
          {qDebounced ? "Aucune commande ne correspond." : (showArchived ? "Aucune archive." : "Aucune commande. Créez la première.")}
        </div>
      )}

      <div className="rounded-xl border overflow-hidden">
        {items.map((it) => {
          const isChecked = selectedIds.has(it.id);
          const nProducts = (it.products || []).length;
          return (
            <div key={it.id}
                 className={"flex items-start gap-3 p-3 border-b last:border-0 hover:bg-muted/30 " + (isChecked ? "bg-primary/5" : "")}
                 data-testid={`po-row-${it.id}`}>
              <input type="checkbox" checked={isChecked}
                     onChange={() => {
                       const s = new Set(selectedIds);
                       isChecked ? s.delete(it.id) : s.add(it.id);
                       setSelectedIds(s);
                     }} className="mt-1"
                     data-testid={`po-select-${it.id}`}/>
              <button onClick={() => setSelected(it)} className="flex-1 text-left min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm truncate">{productsPreview(it.products)}</span>
                  {nProducts > 1 && <span className="text-[0.6rem] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 font-semibold">{nProducts} produits</span>}
                  <StatusBadge ordered_at={it.ordered_at}/>
                  {it.archived && <span className="text-[0.6rem] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300 font-semibold">ARCHIVÉE</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 truncate">
                  <b>{it.client_name}</b> · demandée par {it.requester_tech_name || "—"}
                  {it.invoice_or_quote_number && <> · #{it.invoice_or_quote_number}</>}
                  <> · {dateOrAbs(it.requested_at || it.created_at)}</>
                  {it.ordered_at && <span className="text-emerald-700"> · commandée {absTime(it.ordered_at)}{it.ordered_by_name ? ` par ${it.ordered_by_name}` : ""}</span>}
                </div>
                {it.tracking_info && (
                  <div className="text-xs mt-0.5 text-blue-700 truncate inline-flex items-center gap-1">
                    <ClipboardText size={11}/> {it.tracking_info}
                  </div>
                )}
              </button>
              {isAdmin && !it.ordered_at && !it.archived && (
                <Button size="sm" onClick={() => setMarkModal({ item: it, tracking_info: "" })}
                        data-testid={`po-mark-${it.id}`}>
                  <CheckCircle size={13} className="mr-1"/> Marquer commandée
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* v287 — Pagination controls (25/page) */}
      {total > 0 && (
        <div className="flex items-center justify-between mt-3 text-sm">
          <div className="text-muted-foreground" data-testid="po-total">{total} commande(s)</div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="po-prev-page">Précédent</Button>
            <span className="mono" data-testid="po-page-indicator">Page {page} / {pages}</span>
            <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="po-next-page">Suivant</Button>
          </div>
        </div>
      )}

      {/* Detail modal */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  <ShoppingCart size={20}/> Commande pour {selected.client_name}
                  <StatusBadge ordered_at={selected.ordered_at}/>
                  {selected.archived && <span className="text-[0.65rem] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300 font-semibold">ARCHIVÉE</span>}
                </DialogTitle>
                <DialogDescription>Créée par {selected.author_name} · MAJ {absTime(selected.updated_at)}</DialogDescription>
              </DialogHeader>

              <div>
                <Label>Produits ({(selected.products || []).length})</Label>
                <div className="rounded-lg border overflow-hidden mt-1">
                  {(selected.products || []).map((p, i) => (
                    <div key={i} className="px-3 py-2 border-b last:border-0 text-sm flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{p.name}</div>
                        {p.type && <div className="text-xs text-muted-foreground">{p.type}</div>}
                        {p.url && (
                          <a href={p.url} target="_blank" rel="noopener noreferrer"
                             className="text-xs text-primary hover:underline inline-flex items-center gap-1 break-all">
                            <LinkIcon size={11}/> {p.url}
                          </a>
                        )}
                        {(fmtPrice(p.purchase_price) || fmtPrice(p.sale_price)) && (
                          <div className="mt-1 flex flex-wrap gap-3 text-[0.7rem]" data-testid={`po-detail-product-${i}-prices`}>
                            {fmtPrice(p.purchase_price) && (
                              <span className="text-teal-700 dark:text-teal-300"><b>Achat :</b> {fmtPrice(p.purchase_price)}</span>
                            )}
                            {fmtPrice(p.sale_price) && (
                              <span className="text-orange-800 dark:text-orange-300"><b>Vente :</b> {fmtPrice(p.sale_price)}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="text-sm font-semibold mono shrink-0">×{p.quantity || 1}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mt-3">
                <div><Label>Technicien demandeur</Label><div className="font-medium mt-1">{selected.requester_tech_name || "—"}</div></div>
                <div><Label>N° facture / devis</Label><div className="mono text-xs mt-1">{selected.invoice_or_quote_number || "—"}</div></div>
                <div><Label>Date demande</Label><div className="mt-1">{dateOrAbs(selected.requested_at || selected.created_at)}</div></div>
                {selected.ordered_at && (
                  <>
                    <div><Label>Date commande</Label><div className="mt-1 text-emerald-700 font-medium">{absTime(selected.ordered_at)}</div></div>
                    <div><Label>Commandée par</Label><div className="mt-1">{selected.ordered_by_name || "—"}</div></div>
                  </>
                )}
              </div>
              {selected.tracking_info && (
                <div className="mt-3"><Label>Info de suivi</Label>
                  <div className="text-sm bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded p-2 mt-1 whitespace-pre-wrap">{selected.tracking_info}</div>
                </div>
              )}
              {selected.notes && (
                <div className="mt-3"><Label>Notes</Label>
                  <div className="text-sm whitespace-pre-wrap mt-1">{selected.notes}</div>
                </div>
              )}
              <DialogFooter className="flex-nowrap gap-2 overflow-x-auto">
                {isAdmin && !selected.ordered_at && !selected.archived && (
                  <Button size="sm" onClick={() => setMarkModal({ item: selected, tracking_info: selected.tracking_info || "" })}
                          data-testid="po-mark-modal-btn" className="whitespace-nowrap">
                    <CheckCircle size={14} className="mr-1"/> Marquer commandée
                  </Button>
                )}
                {isAdmin && selected.ordered_at && (
                  <Button size="sm" variant="outline"
                          onClick={() => setMarkModal({ item: selected, tracking_info: selected.tracking_info || "", trackingOnly: true })}
                          data-testid="po-edit-tracking-btn" className="whitespace-nowrap">
                    <ClipboardText size={14} className="mr-1"/> N° de suivi
                  </Button>
                )}
                {isAdmin && selected.ordered_at && (
                  <Button size="sm" variant="outline" onClick={() => unmarkOrdered(selected)} data-testid="po-unmark-btn" className="whitespace-nowrap">
                    <ArrowCounterClockwise size={14} className="mr-1"/> Annuler commande
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => { openEdit(selected); setSelected(null); }} data-testid="po-edit-btn" className="whitespace-nowrap">
                  <PencilSimple size={14} className="mr-1"/> Modifier
                </Button>
                {selected.archived ? (
                  <Button size="sm" variant="outline" onClick={() => toggleArchive(selected, false)} data-testid="po-unarchive-btn" className="whitespace-nowrap">
                    <ArrowCounterClockwise size={14} className="mr-1"/> Restaurer
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => toggleArchive(selected, true)} data-testid="po-archive-btn" className="whitespace-nowrap">
                    <Archive size={14} className="mr-1"/> Archiver
                  </Button>
                )}
                {isAdmin && (
                  <Button size="sm" variant="destructive" onClick={() => setConfirmDelete(selected)} data-testid="po-delete-btn" className="whitespace-nowrap">
                    <Trash size={14} className="mr-1"/> Supprimer
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Editor */}
      <Dialog open={!!editor} onOpenChange={(v) => !v && setEditor(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editor?.mode === "create" ? "Nouvelle demande de commande" : "Modifier la demande"}</DialogTitle>
          </DialogHeader>
          {editor && (
            <div className="space-y-3">
              <div>
                <Label>Client (nom/prénom ou entreprise) *</Label>
                <Input value={editor.client_name} onChange={(e) => setEditor({...editor, client_name: e.target.value})}
                       placeholder="Ex: ACME SAS ou Jean Dupont" data-testid="po-editor-client"/>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Technicien demandeur</Label>
                  <Select value={editor.requester_tech_id}
                          onValueChange={(v) => {
                            const t = techs.find(x => x.id === v);
                            setEditor({...editor, requester_tech_id: v, requester_tech_name: t?.full_name || ""});
                          }}>
                    <SelectTrigger data-testid="po-editor-tech"><SelectValue placeholder="Sélectionner…"/></SelectTrigger>
                    <SelectContent>
                      {techs.map(t => <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>N° facture / devis</Label>
                  <Input value={editor.invoice_or_quote_number}
                         onChange={(e) => setEditor({...editor, invoice_or_quote_number: e.target.value})}
                         placeholder="FA-2026-0142" data-testid="po-editor-invoice"/>
                </div>
              </div>
              <div>
                <Label>Date de la demande</Label>
                <Input type="date" value={editor.requested_at}
                       onChange={(e) => setEditor({...editor, requested_at: e.target.value})}
                       data-testid="po-editor-date"/>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Produits à commander * ({editor.products.length})</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addProduct} data-testid="po-editor-add-product">
                    <Plus size={13} className="mr-1"/> Ajouter un produit
                  </Button>
                </div>
                <div className="space-y-2">
                  {editor.products.map((p, i) => (
                    <div key={i} className="rounded-lg border p-3 space-y-2 bg-muted/20" data-testid={`po-editor-product-${i}`}>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Produit #{i + 1}</span>
                        {editor.products.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeProduct(i)}
                                  data-testid={`po-editor-remove-product-${i}`}>
                            <X size={13}/>
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                        <div className="md:col-span-3">
                          <Label className="text-xs">Nom du produit *</Label>
                          <Input value={p.name} onChange={(e) => setProduct(i, { name: e.target.value })}
                                 placeholder="Ex: Dell U2723QE, Office 365 E3…"
                                 data-testid={`po-editor-product-${i}-name`}/>
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-xs">Type</Label>
                          <Input value={p.type} onChange={(e) => setProduct(i, { type: e.target.value })}
                                 placeholder="Écran, Licence…"
                                 data-testid={`po-editor-product-${i}-type`}/>
                        </div>
                        <div>
                          <Label className="text-xs">Qté</Label>
                          <Input type="number" min="1" step="1" value={p.quantity}
                                 onChange={(e) => setProduct(i, { quantity: e.target.value })}
                                 data-testid={`po-editor-product-${i}-qty`}/>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Lien produit</Label>
                        <Input value={p.url} onChange={(e) => setProduct(i, { url: e.target.value })}
                               placeholder="https://fournisseur.com/…"
                               data-testid={`po-editor-product-${i}-url`}/>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Tarif achat (€ HT)</Label>
                          <Input type="number" min="0" step="0.01" value={p.purchase_price}
                                 onChange={(e) => setProduct(i, { purchase_price: e.target.value })}
                                 placeholder="0,00"
                                 data-testid={`po-editor-product-${i}-purchase-price`}/>
                        </div>
                        <div>
                          <Label className="text-xs">Tarif vente (€ HT)</Label>
                          <Input type="number" min="0" step="0.01" value={p.sale_price}
                                 onChange={(e) => setProduct(i, { sale_price: e.target.value })}
                                 placeholder="0,00"
                                 data-testid={`po-editor-product-${i}-sale-price`}/>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label>Notes (facultatif)</Label>
                <Textarea rows={3} value={editor.notes} onChange={(e) => setEditor({...editor, notes: e.target.value})}
                          placeholder="Info contextuelle, urgence, budget…" data-testid="po-editor-notes"/>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(null)} disabled={busy}>Annuler</Button>
            <Button onClick={save} disabled={busy} data-testid="po-editor-save">
              {busy ? "…" : editor?.mode === "create" ? "Créer" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark ordered modal — dual mode: initial "mark ordered" (v220) or tracking-only edit (v292) */}
      <Dialog open={!!markModal} onOpenChange={(v) => !v && setMarkModal(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {markModal?.trackingOnly ? <><ClipboardText size={18}/> Numéro de suivi</> : <><CheckCircle size={18}/> Marquer commandée</>}
            </DialogTitle>
            <DialogDescription>
              {markModal?.trackingOnly
                ? "Mise à jour du numéro de suivi uniquement — aucune notification email ne sera envoyée (la commande a déjà été marquée)."
                : "La date sera enregistrée à maintenant. Le technicien demandeur recevra automatiquement un email avec l'info de suivi ci-dessous (si activé dans Paramètres › Templates)."}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>{markModal?.trackingOnly ? "Info de suivi" : "Info de suivi (facultatif)"}</Label>
            <Textarea rows={4} value={markModal?.tracking_info || ""}
                      onChange={(e) => setMarkModal({...markModal, tracking_info: e.target.value})}
                      placeholder="Ex: Chronopost XY 8342 1976 4 FR — livraison prévue mardi"
                      data-testid="po-mark-tracking"/>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkModal(null)}>Annuler</Button>
            <Button onClick={markOrdered} disabled={busy} data-testid="po-mark-confirm">
              {markModal?.trackingOnly
                ? <><ClipboardText size={14} className="mr-1"/> {busy ? "…" : "Enregistrer le suivi"}</>
                : <><CheckCircle size={14} className="mr-1"/> {busy ? "…" : "Confirmer la commande"}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => { del(confirmDelete.id); setConfirmDelete(null); }}
        title="Supprimer cette commande ?"
        description={`Action définitive — ${productsPreview(confirmDelete?.products)}`}
      />
      <ConfirmDialog
        open={confirmBulk}
        onCancel={() => setConfirmBulk(false)}
        onConfirm={bulkArchive}
        title={showArchived ? "Restaurer les commandes ?" : "Archiver les commandes ?"}
        description={`${selectedIds.size} commande(s) sélectionnée(s)`}
      />
    </div>
  );
}
